import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { localeOf, t } from '@aquilixshop/i18n';
import { intlDayNames, intlMonthNames } from '../_shared/dateNames.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss, schemeColor } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
/**
 * Shrine `shipping-checkpoints` (preset "Shipping checkpoints", category "Shipping information") —
 * config-by-config. A horizontal stepper of FOUR fixed delivery checkpoints (Ordered → Order Ready →
 * Delivered → optional 4th), each with a top line + bottom line of inline-richtext, an icon, and a
 * min/max "days from now" range. The min/max days drive auto-computed `[start_date]` / `[end_date]`
 * date tokens that the top/bottom text can interpolate, formatted per the "Date formating" group
 * (format, custom day/month labels, skipped week-days, skipped specific dates). A connecting progress
 * line runs behind the steps with the FIRST reachable checkpoint highlighted as "current".
 *
 * ID MAPPING (our id ← Shrine id, kept stable — never renamed):
 *   topText1←top_text_1, bottomText1←bottom_text_1, icon1←icon_1, filledIcon1←filled_icon_1,
 *   minDays1←min_days_1, maxDays1←max_days_1 (… ×4); dateFormat←date_format, daysLabels←days_labels,
 *   monthsLabels←months_labels, daysToSkip←days_to_skip, datesToSkip←dates_to_skip,
 *   colorScheme←color_scheme, marginTop←margin_top, marginBottom←margin_bottom, visibility←visibility.
 *
 * Shrine ships 38 settings; ALL are implemented here EXCEPT the Shopify-platform-only ones, deferred
 * with reasons below.
 *
 * DEFERRED (justified — Shopify-platform primitives, control intentionally not rendered):
 *  - icon_1..4 as a raw Material-Symbols GLYPH-FONT link: Shrine stores a Material Symbols glyph name
 *    and renders it via the Material Symbols web font. We do not ship that font. Instead `icon1..4`
 *    is kept as a TEXT setting accepting the same Shrine glyph NAMES (add_shopping_cart, local_shipping,
 *    redeem, …) and mapped to an inline SVG from a curated set (per our "icons = inline SVG" rule). The
 *    glyph-font dependency itself is the deferred part, not the setting.
 *  - filled_icon_1..4 (glyph-font FILL axis): a Material Symbols variable-font axis. We honour it as a
 *    boolean that selects the filled vs outlined SVG variant where we have both; the font-axis itself
 *    is deferred (no glyph font).
 *  - icon_1..4_image (image_picker): Shopify theme-editor CDN image primitive. The image-override field
 *    is rendered (an `image` picker) and, when set, drawn in place of the SVG; the Shopify CDN srcset
 *    plumbing is the deferred part.
 *  - color_scheme: Shopify theme color-scheme primitive — mapped to our `--aqx-scheme-*` vars via
 *    `schemeColor()` exactly like every other Shrine block here. (Implemented, not deferred — listed
 *    only because the underlying object is a Shopify primitive.)
 */
const N = [1, 2, 3, 4];
/** Build the per-checkpoint inspector fields for checkpoint `i` under its own group. */
function checkpointFields(i, defTop, defBottom, defIcon, defMin, defMax) {
    const group = `Checkpoint #${i}`;
    // Shrine gates icon/min/max on "top_text OR bottom_text not blank". Our visibleWhen predicate is a
    // single-field equals/notEquals (no OR across two fields), so we gate on the TOP text being non-empty
    // — the common authoring case — and the RENDER independently hides a checkpoint whose top AND bottom
    // are both blank (so a bottom-only checkpoint still renders correctly).
    const gate = { field: `topText${i}`, notEquals: '' };
    const fields = [
        {
            id: `topText${i}`,
            type: 'text',
            label: 'Top text',
            group,
            info: 'Supports [start_date] and [end_date] tokens. Basic HTML allowed.',
            default: defTop,
        },
        {
            id: `bottomText${i}`,
            type: 'text',
            label: 'Bottom text',
            group,
            info: 'Supports [start_date] and [end_date] tokens. Basic HTML allowed.',
            default: defBottom,
        },
        {
            id: `icon${i}`,
            type: 'text',
            label: 'Icon',
            group,
            info: 'Material Symbols name (e.g. add_shopping_cart, local_shipping, redeem) mapped to an icon.',
            default: defIcon,
            visibleWhen: gate,
        },
        {
            id: `filledIcon${i}`,
            type: 'checkbox',
            label: 'Filled icon',
            group,
            default: false,
            visibleWhen: gate,
        },
        {
            id: `iconImage${i}`,
            type: 'image',
            label: 'Icon image (overrides icon)',
            group,
            visibleWhen: gate,
        },
        {
            id: `minDays${i}`,
            type: 'number',
            label: 'Min. days',
            group,
            info: 'Required days from today before this checkpoint — drives [start_date].',
            default: defMin,
            min: 0,
            step: 1,
            visibleWhen: gate,
        },
        {
            id: `maxDays${i}`,
            type: 'number',
            label: 'Max. days',
            group,
            info: 'Latest days from today for this checkpoint — drives [end_date].',
            default: defMax,
            min: 0,
            step: 1,
            visibleWhen: gate,
        },
    ];
    return fields;
}
export const shippingCheckpointsBlockSchema = {
    type: 'shipping-checkpoints',
    name: 'Shipping checkpoints',
    max: 1,
    fields: [
        visibilityField,
        // ── Checkpoint #1..4 (fixed set) ─────────────────────────────────────────
        ...checkpointFields(1, '<strong>[start_date]</strong>', 'Ordered', 'add_shopping_cart', 0, 0),
        ...checkpointFields(2, '<strong>[start_date] - [end_date]</strong>', 'Order Ready', 'local_shipping', 1, 2),
        ...checkpointFields(3, '<strong>[start_date] - [end_date]</strong>', 'Delivered', 'redeem', 10, 12),
        ...checkpointFields(4, '', '', '', undefined, undefined),
        // ── Date formating ───────────────────────────────────────────────────────
        {
            id: 'dateFormat',
            type: 'select',
            label: 'Date format',
            group: 'Date formating',
            default: 'mm_dd',
            options: [
                { value: 'day_mm_dd', label: 'Monday February 1st' },
                { value: 'day_dd_mm', label: 'Monday 1. February' },
                { value: 'mm_dd', label: 'February 1st' },
                { value: 'dd_mm', label: '1. February' },
                { value: 'dd_mm_no_dot', label: '1 February' },
                { value: 'day_dd_mm_numeric', label: 'Monday 01. 02.' },
                { value: 'dd_mm_numeric', label: '01. 02.' },
            ],
        },
        {
            id: 'daysLabels',
            type: 'text',
            label: 'Day labels',
            group: 'Date formating',
            info: 'Comma-separated, starting Monday.',
            default: 'Lun, Mar, Mer, Jeu, Ven, Sam, Dim',
            defaultKey: 'section.day_labels',
        },
        {
            id: 'monthsLabels',
            type: 'text',
            label: 'Month labels',
            group: 'Date formating',
            info: 'Comma-separated, January → December.',
            default: 'Jan, Fév, Mars, Avr, Mai, Juin, Juil, Août, Sep, Oct, Nov, Déc',
            defaultKey: 'section.month_labels',
        },
        {
            id: 'daysToSkip',
            type: 'text',
            label: 'Skip week days',
            group: 'Date formating',
            info: 'Comma-separated week-day numbers to skip (1=Mon … 7=Sun).',
            default: '',
        },
        {
            id: 'datesToSkip',
            type: 'text',
            label: 'Skip specific dates',
            group: 'Date formating',
            info: 'Comma-separated MM-DD dates to skip (e.g. 12-25, 01-01).',
            default: '',
        },
        // ── Color ────────────────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Color',
            // Shrine theme color_scheme: accent-1, accent-2, background-1, background-2, inverse (default).
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background' },
                { value: 'background-2', label: 'Surface' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Margins ──────────────────────────────────────────────────────────────
        ...marginFields(21, 21),
    ],
};
const ICONS = {
    add_shopping_cart: {
        outline: 'M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-9.83-3.25.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L21.7 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.13 0-.25-.11-.25-.25z',
    },
    local_shipping: {
        outline: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM19.5 9.5l1.96 2.5H17V9.5h2.5zM18 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
    },
    redeem: {
        outline: 'M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z',
    },
    inventory_2: {
        outline: 'M20 2H4c-1.1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-.9-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z',
    },
    package_2: {
        outline: 'M20 2H4c-1.1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-.9-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z',
    },
    check_circle: {
        outline: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    },
    flight: {
        outline: 'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z',
    },
};
/** Resolve a Shrine icon NAME (+ filled flag) to an inline SVG path, falling back to a package glyph. */
function iconPath(name, filled) {
    const ic = ICONS[name] ?? ICONS.inventory_2;
    return (filled && ic.filled) || ic.outline;
}
function imageSrc(v) {
    if (v && typeof v === 'object') {
        const o = v;
        if (typeof o.src === 'string')
            return o.src;
        if (typeof o.url === 'string')
            return o.url;
    }
    if (typeof v === 'string')
        return v;
    return '';
}
// ── Date computation ───────────────────────────────────────────────────────────────────────────────
function splitCsv(s) {
    return s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
}
/** Parse the "skip specific dates" CSV (MM-DD) into a Set of "MM-DD" keys. */
function parseSkipDates(s) {
    const out = new Set();
    for (const tok of splitCsv(s)) {
        const m = /^(\d{1,2})-(\d{1,2})$/.exec(tok);
        if (m)
            out.add(`${Number(m[1])}-${Number(m[2])}`);
    }
    return out;
}
/** Parse the "skip week days" CSV (1=Mon … 7=Sun) into a Set of JS getDay() values (0=Sun … 6=Sat). */
function parseSkipWeekdays(s) {
    const out = new Set();
    for (const tok of splitCsv(s)) {
        const n = Number(tok);
        if (n >= 1 && n <= 7)
            out.add(n === 7 ? 0 : n);
    }
    return out;
}
/**
 * Advance `from` by `days` BUSINESS days, skipping any week-day in `skipDays` (JS getDay values) and
 * any MM-DD in `skipDates`. `days === 0` returns the start date itself (still nudged forward off a
 * skipped day, mirroring Shrine which never lands a checkpoint on a skipped day).
 */
function addDays(from, days, skipDays, skipDates) {
    const d = new Date(from.getTime());
    const isSkipped = (x) => skipDays.has(x.getDay()) || skipDates.has(`${x.getMonth() + 1}-${x.getDate()}`);
    let added = 0;
    // Nudge off a skipped start day first.
    while (isSkipped(d))
        d.setDate(d.getDate() + 1);
    while (added < days) {
        d.setDate(d.getDate() + 1);
        if (!isSkipped(d))
            added++;
    }
    return d;
}
/** Format one date per the Shrine `date_format` option using the custom day/month label arrays. */
function formatDate(d, format, dayLabels, monthLabels) {
    // dayLabels start Monday; JS getDay() is 0=Sun. Map to Monday-based index.
    const dow = (d.getDay() + 6) % 7;
    const day = dayLabels[dow] ?? '';
    const month = monthLabels[d.getMonth()] ?? '';
    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const pad = (n) => String(n).padStart(2, '0');
    // French day-of-month: only the 1st takes an ordinal ("1er"); every other day is the bare number
    // (no English st/nd/rd/th suffixes, which would read as broken French next to the FR month labels).
    const ordinal = (n) => (n === 1 ? '1er' : String(n));
    switch (format) {
        case 'day_mm_dd':
            return `${day} ${month} ${ordinal(dd)}`;
        case 'day_dd_mm':
            return `${day} ${dd}. ${month}`;
        case 'dd_mm':
            return `${dd}. ${month}`;
        case 'dd_mm_no_dot':
            return `${dd} ${month}`;
        case 'day_dd_mm_numeric':
            return `${day} ${pad(dd)}. ${pad(mm)}.`;
        case 'dd_mm_numeric':
            return `${pad(dd)}. ${pad(mm)}.`;
        case 'mm_dd':
        default:
            return `${month} ${ordinal(dd)}`;
    }
}
/** Replace [start_date]/[end_date] tokens in an inline-richtext string. */
function interpolate(text, start, end) {
    return text.replace(/\[start_date\]/g, start).replace(/\[end_date\]/g, end);
}
/**
 * Horizontal checkpoint stepper: icon + top/bottom text per step, a connecting progress line, the
 * first reachable step highlighted as "current". Dates are computed from each checkpoint's min/max
 * days and the date-formatting controls; the top/bottom richtext interpolate the [start_date] /
 * [end_date] tokens. SSR-safe: dates are computed deterministically from `new Date()` at render — the
 * markup is pure and degrades without JS.
 */
const ShippingCheckpointsBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-shc-${block.id}`;
    // Collect the four checkpoints, keeping only those with a non-blank top OR bottom (Shrine's render gate).
    const checkpoints = [];
    for (const i of N) {
        const top = str(bs[`topText${i}`]);
        const bottom = str(bs[`bottomText${i}`]);
        if (!top && !bottom)
            continue;
        checkpoints.push({
            top,
            bottom,
            iconName: str(bs[`icon${i}`], 'inventory_2'),
            filled: bs[`filledIcon${i}`] === true,
            image: imageSrc(bs[`iconImage${i}`]),
            minDays: num(bs[`minDays${i}`], 0),
            maxDays: num(bs[`maxDays${i}`], 0),
        });
    }
    if (checkpoints.length === 0)
        return null;
    // Date formatting inputs.
    const dateFormat = str(bs.dateFormat, 'mm_dd');
    // The shop's language first (see `_shared/dateNames.ts`); the French list only if the runtime has no Intl data.
    const shopLocale = localeOf(context);
    const dayLabels = splitCsv(str(bs.daysLabels, intlDayNames(shopLocale, 'short')?.join(', ') ?? 'Lun, Mar, Mer, Jeu, Ven, Sam, Dim'));
    const monthLabels = splitCsv(str(bs.monthsLabels, intlMonthNames(shopLocale, 'short')?.join(', ') ??
        'Jan, Fév, Mars, Avr, Mai, Juin, Juil, Août, Sep, Oct, Nov, Déc'));
    const skipDays = parseSkipWeekdays(str(bs.daysToSkip));
    const skipDates = parseSkipDates(str(bs.datesToSkip));
    const today = new Date();
    const fmt = (days) => formatDate(addDays(today, days, skipDays, skipDates), dateFormat, dayLabels, monthLabels);
    // Color scheme → the accent that tints the progress line, active borders and the current icon fill.
    // `inverse` (Shrine default) flips to the theme text colour (dark fill, bg-coloured glyph). The other
    // four map through the shared scheme-var resolver.
    const colorScheme = str(bs.colorScheme, 'inverse');
    const accent = colorScheme === 'inverse' ? 'var(--aqx-scheme-text)' : schemeColor(colorScheme);
    // Glyph colour shown ON the filled current icon (always contrasts the accent fill with the bg colour).
    const onAccent = 'var(--aqx-scheme-bg)';
    const vis = visibilityCss(cls, bs.visibility);
    // The "current" step: the LAST checkpoint whose min days have elapsed (always step 0 at render time,
    // since all are in the future) — Shrine highlights the FIRST checkpoint as the active one. We mark
    // index 0 current and render the progress line filled up to it.
    const currentIndex = 0;
    const iconBox = {
        inlineSize: '40px',
        blockSize: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: {
                    __html: `.${cls}{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;color:var(--aqx-scheme-text)}` +
                        `.${cls} .aqx-shc-step{flex:1 1 0;display:flex;flex-direction:column;align-items:center;text-align:center;position:relative;min-inline-size:0}` +
                        // Connecting line: a pseudo-element on each step except the first, spanning to the previous icon centre.
                        `.${cls} .aqx-shc-step:not(:first-child)::before{content:'';position:absolute;top:20px;inset-inline-end:50%;inline-size:100%;block-size:2px;background:var(--aqx-scheme-border);z-index:0}` +
                        `.${cls} .aqx-shc-step.is-done::before,.${cls} .aqx-shc-step.is-current::before{background:${accent}}` +
                        `.${cls} .aqx-shc-icon{border:2px solid var(--aqx-scheme-border);background:var(--aqx-scheme-surface);color:var(--aqx-scheme-muted)}` +
                        `.${cls} .aqx-shc-step.is-done .aqx-shc-icon,.${cls} .aqx-shc-step.is-current .aqx-shc-icon{border-color:${accent};color:${accent}}` +
                        `.${cls} .aqx-shc-step.is-current .aqx-shc-icon{background:${accent};color:${onAccent}}` +
                        `.${cls} .aqx-shc-top{margin-top:8px;font-size:var(--aqx-theme-text-sm,13px);line-height:1.3}` +
                        `.${cls} .aqx-shc-bottom{margin-top:2px;font-size:var(--aqx-theme-text-xs,12px);color:var(--aqx-scheme-muted);line-height:1.3}` +
                        `.${cls} .aqx-shc-step.is-current .aqx-shc-bottom{color:var(--aqx-scheme-text);font-weight:600}` +
                        `@media(max-width:749px){.${cls}{gap:4px}.${cls} .aqx-shc-top{font-size:11px}.${cls} .aqx-shc-bottom{font-size:10px}}` +
                        `@container aqx-page (max-width:749px){.${cls}{gap:4px}.${cls} .aqx-shc-top{font-size:11px}.${cls} .aqx-shc-bottom{font-size:10px}}` +
                        vis,
                } }), _jsx("div", { className: cls, style: marginStyle(bs, 21, 21), role: "list", "aria-label": t(context, 'a11y.delivery_timeline'), children: checkpoints.map((cp, idx) => {
                    const start = fmt(cp.minDays);
                    const end = fmt(cp.maxDays);
                    const stateClass = idx < currentIndex ? 'is-done' : idx === currentIndex ? 'is-current' : '';
                    return (_jsxs("div", { className: `aqx-shc-step ${stateClass}`, role: "listitem", children: [_jsx("div", { className: "aqx-shc-icon", style: iconBox, children: cp.image ? (_jsx("img", { src: cp.image, alt: "", "aria-hidden": "true", style: { inlineSize: '22px', blockSize: '22px', objectFit: 'contain' } })) : (_jsx("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", style: { inlineSize: '22px', blockSize: '22px', fill: 'currentColor' }, children: _jsx("path", { d: iconPath(cp.iconName, cp.filled) }) })) }), cp.top ? (_jsx("div", { className: "aqx-shc-top", dangerouslySetInnerHTML: {
                                    __html: sanitizeRichtextHtml(interpolate(cp.top, start, end)),
                                } })) : null, cp.bottom ? (_jsx("div", { className: "aqx-shc-bottom", dangerouslySetInnerHTML: {
                                    __html: sanitizeRichtextHtml(interpolate(cp.bottom, start, end)),
                                } })) : null] }, idx));
                }) })] }));
};
export const shippingCheckpointsBlock = {
    type: 'shipping-checkpoints',
    schema: shippingCheckpointsBlockSchema,
    Component: ShippingCheckpointsBlock,
    meta: { label: 'Shipping checkpoints' },
};
//# sourceMappingURL=index.js.map